import { animate, state, style, transition, trigger } from '@angular/animations';
import { DataSource } from '@angular/cdk/collections';
import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { OrganizationRiderService, Payment, PaymentAccount, Transaction } from '@uzurv/clara';
import { cloneDeep } from 'lodash';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-payment-history',
  templateUrl: './payment-history.component.html',
  styleUrls: ['./payment-history.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
    trigger('indicatorRotate', [
      state('closed', style({ transform: 'rotate(0deg)' })),
      state('open', style({ transform: 'rotate(180deg)' })),
      transition('open <=> closed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)'))
    ])
  ]
})
export class PaymentHistoryComponent implements OnChanges {
  @ViewChild('paymentHistoryResultsPaginator', { static: true }) paymentHistoryResultsPaginator: MatPaginator;

  @Input() paymentAccounts: Array<PaymentAccount>;
  @Input() payments: Array<Payment>;
  @Input() expandedPayment: Payment;
  @Input() expandedPaymentTransactions: Array<Transaction>;
  @Input() loadingTransactions: boolean;

  @Output() pageListClicked: EventEmitter<PageEvent> = new EventEmitter();
  @Output() editPaymentAccount: EventEmitter<PaymentAccount> = new EventEmitter();
  @Output() newPayment: EventEmitter<any> = new EventEmitter();
  @Output() voidPayment: EventEmitter<Payment> = new EventEmitter();
  @Output() resubmitPayment: EventEmitter<Payment> = new EventEmitter();
  @Output() refundPayment: EventEmitter<Payment> = new EventEmitter();
  @Output() expandPayment: EventEmitter<Payment> = new EventEmitter();

  panelOpenState = false;
  chargeDataSource: DataSource<Payment>;
  paymentColumns;
  spinnerLoading: boolean;
  loadingPaymentHistory = false;
  pageSize: number = 20;

  chargesColumns = [
    {
      columnDef: 'processedAmount',
      title: 'Processed Amount',
      cell: (reservationCharge: Payment) => `${reservationCharge.processedAmount}`
    },
    { columnDef: 'amount', title: 'Amount', cell: (reservationCharge: Payment) => `${reservationCharge.amount}` },
    { columnDef: 'date', title: 'Date', cell: (reservationCharge: Payment) => `${reservationCharge.createdDate}` },
    {
      columnDef: 'status',
      title: 'status',
      cell: (reservationCharge: Payment) => `${reservationCharge.paymentStatus.name}`
    },
    {
      columnDef: 'description',
      title: 'description',
      cell: (reservationCharge: Payment) => `${reservationCharge.paymentDescription}`
    },
    { columnDef: 'actions', title: 'Actions', cell: (reservationCharge: Payment) => '' }
  ];

  constructor(
    private currencyPipe: CurrencyPipe,
    private organizationRiderService: OrganizationRiderService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payments'] && changes['payments'].currentValue) {
      this.paymentColumns = this.chargesColumns.map(c => c.columnDef);
      this.chargeDataSource = new PaymentDetailDataSource(this.payments);
    }

    if (changes['paymentAccounts'] && changes['paymentAccounts'].currentValue) {
      if (this.expandedPayment) {
        this.paymentAccounts.forEach((paymentAccount: PaymentAccount) => {
          if (this.expandedPayment.paymentAccount.id === paymentAccount.id) {
            this.expandedPayment.paymentAccount = paymentAccount;
            return;
          }
        });
      }
    }
  }

  handleRefund(payment: Payment): void {
    this.refundPayment.emit(cloneDeep(payment));
  }

  handleResubmit(payment: Payment): void {
    this.resubmitPayment.emit(cloneDeep(payment));
  }

  handleGoToReservation(reservationId: number): void {
    this.router.navigateByUrl(`/rides/res-${reservationId}`);
  }

  handleVoidPayment(payment: Payment): void {
    this.voidPayment.emit(payment);
  }

  handleExpandPayment(payment: Payment): void {
    this.expandPayment.emit(payment);
  }

  handleNewPayment(): void {
    this.newPayment.emit();
  }

  handleEditPaymentAccount(paymentAccount: PaymentAccount): void {
    this.editPaymentAccount.emit(paymentAccount);
  }

  handlePageChange(pageEvent: PageEvent) {
    this.pageListClicked.emit(pageEvent);
  }
}

export class PaymentDetailDataSource extends DataSource<Payment> {
  private dataBase: any;
  constructor(data: Array<Payment>) {
    super();
    this.dataBase = data;
  }

  /** Connect function called by the table to retrieve one stream containing the data to render. */
  connect(): Observable<Array<Payment>> {
    const rows = [];
    this.dataBase.forEach(paymentDetail => rows.push(paymentDetail));
    return of(rows);
  }

  disconnect() {}
}
